import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiCashCheck, mdiQrcode, mdiEmailFastOutline } from "@mdi/js";
import QRCode from "qrcode";
import { toast } from "react-toastify";
import BaseModal from "../BaseModal";
import EmailReviewModal from "../communications/EmailReviewModal";
import { classApi } from "../../api/classes";
import { formatCurrencyFromMinor } from "../../utils/currency";
import usePayoutsReady from "../../hooks/usePayoutsReady";
import "./CollectAdjustmentModal.css";

/**
 * Lean "collect" modal for an EXISTING outstanding payment/adjustment (e.g. a walk-in
 * drop-in fee). It does NOT create a charge — it settles one that already exists, by id.
 * Three ways to collect: mark paid (cash/eftpos/bank), generate a Stripe QR, or email a
 * payment-link reminder. Reusable by the check-in card and the Payments audit tab.
 */
const METHOD_LABELS = { cash: "Cash", eftpos: "EFTPOS", bank_transfer: "Bank transfer", stripe: "Card (Stripe)", external: "External" };

function CollectAdjustmentModal({ isOpen, onClose, paymentId, orgId, studentName, amountMinor, currency, paymentStatus, paidOutsidePlatform, paymentMethod, paidAt, reference, onSettled }) {
    const [busy, setBusy] = useState(null); // 'paid' | 'qr' | null
    const [method, setMethod] = useState("cash");
    const [showEmailReview, setShowEmailReview] = useState(false);
    const [isConfirmingEmail, setIsConfirmingEmail] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [qrUrl, setQrUrl] = useState(null);

    // false = positively not able to take card payments (no active Stripe account);
    // null = unknown (loading/fetch failed) — never block on unknown.
    const payoutsReady = usePayoutsReady(orgId, isOpen);
    const cardPaymentsBlocked = payoutsReady === false;

    const handleMarkPaid = async () => {
        setBusy("paid");
        try {
            const res = await classApi.resolvePriorPayment(
                paymentId, orgId, "paid_external", "Collected at check-in", method
            );
            if (res.ok) {
                toast.success("Marked as paid.");
                if (onSettled) onSettled();
            } else {
                toast.error(res.data?.message || "Failed to mark as paid.");
            }
        } catch {
            toast.error("Failed to mark as paid.");
        } finally {
            setBusy(null);
        }
    };

    const handleGenerateQr = async () => {
        setBusy("qr");
        try {
            const res = await classApi.collectPaymentQr(paymentId);
            if (res.ok && res.data?.qrUrl) {
                const dataUrl = await QRCode.toDataURL(res.data.qrUrl, { width: 220, margin: 2 });
                setQrDataUrl(dataUrl);
                setQrUrl(res.data.qrUrl);
            } else {
                toast.error(res.data?.message || "Failed to generate QR code.");
            }
        } catch {
            toast.error("Failed to generate QR code.");
        } finally {
            setBusy(null);
        }
    };

    // Edit-before-send: EmailReviewModal returns the (possibly edited) subject + body.
    const handleConfirmEmailSend = async (subject, bodyHtml) => {
        setIsConfirmingEmail(true);
        try {
            const res = await classApi.collectPaymentEmail(paymentId, subject, bodyHtml);
            if (res.ok) {
                toast.success("Payment reminder emailed.");
                setShowEmailReview(false);
                // Close the whole collect modal too (same behaviour on check-in + Payments tab).
                if (onClose) onClose();
            } else {
                toast.error(res.data?.message || "Failed to send the reminder.");
            }
        } catch {
            toast.error("Failed to send the reminder.");
        } finally {
            setIsConfirmingEmail(false);
        }
    };

    const amountLabel =
        Number.isFinite(amountMinor) && currency ? formatCurrencyFromMinor(amountMinor, currency) : null;

    const isPaid = paymentStatus === "paid" || paymentStatus === "partial-refund";
    const methodLabel = METHOD_LABELS[paymentMethod] || paymentMethod || null;
    const paidDateStr = paidAt
        ? new Date(paidAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
        : null;

    return (
        <>
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={studentName
                ? `${isPaid ? "Payment" : "Collect payment"} — ${studentName}`
                : (isPaid ? "Payment" : "Collect payment")}
            icon={mdiCashCheck}
            maxWidth={460}
            className="collect-adjustment-modal"
        >
            <div className="collect-adjustment-body">
                {isPaid ? (
                    <div className="collect-adjustment-paid">
                        <p className={`collect-adjustment-paid-label${paidOutsidePlatform ? " collect-adjustment-paid-label--unreconciled" : ""}`}>
                            {paidOutsidePlatform ? "Payment marked as paid" : "Payment received"}
                        </p>
                        {amountLabel && <p className="collect-adjustment-paid-amount">{amountLabel}</p>}
                        {methodLabel && <p className="collect-adjustment-paid-line">Method: {methodLabel}</p>}
                        {paidDateStr && <p className="collect-adjustment-paid-line">Date: {paidDateStr}</p>}
                        {reference && <p className="collect-adjustment-paid-line">Ref: {reference}</p>}
                        {paidOutsidePlatform && (
                            <p className="collect-adjustment-paid-note">
                                Paid outside the platform. Reconcile in Manage Finances whenever you balance your books (optional).
                            </p>
                        )}
                    </div>
                ) : (
                <>
                {amountLabel && (
                    <p className="collect-adjustment-amount">
                        Outstanding: <strong>{amountLabel}</strong>
                    </p>
                )}

                {cardPaymentsBlocked && (
                    <div className="collect-adjustment-warning-banner">
                        <p>
                            <strong>Online payments are not set up yet.</strong> Connect your payout account in{" "}
                            <a href="/manage-finances">Manage Finances</a> to take card payments. You can still use
                            Mark paid to record a payment received outside the platform.
                        </p>
                    </div>
                )}

                {/* Mark paid */}
                <div className="collect-adjustment-section">
                    <label className="collect-adjustment-label">They paid you directly</label>
                    <div className="collect-adjustment-methods">
                        {[
                            { id: "cash", label: "Cash" },
                            { id: "eftpos", label: "EFTPOS" },
                            { id: "bank_transfer", label: "Bank transfer" },
                        ].map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                className={`collect-adjustment-method${method === m.id ? " selected" : ""}`}
                                onClick={() => setMethod(m.id)}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="collect-adjustment-action collect-adjustment-action--paid"
                        onClick={handleMarkPaid}
                        disabled={busy !== null}
                    >
                        <Icon path={mdiCashCheck} size={0.8} />
                        {busy === "paid" ? "Marking..." : "Mark paid"}
                    </button>
                </div>

                {/* Generate QR */}
                <div className="collect-adjustment-section">
                    <label className="collect-adjustment-label">Take instant payment</label>
                    {qrDataUrl ? (
                        <div className="collect-adjustment-qr">
                            <img src={qrDataUrl} alt="Payment QR code" className="collect-adjustment-qr-img" />
                            <p className="collect-adjustment-qr-note">Have them scan to pay by card.</p>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="collect-adjustment-action"
                            onClick={handleGenerateQr}
                            disabled={busy !== null || cardPaymentsBlocked}
                            title={cardPaymentsBlocked ? "Connect your payout account in Manage Finances first" : undefined}
                        >
                            <Icon path={mdiQrcode} size={0.8} />
                            {busy === "qr" ? "Generating..." : "Generate QR code"}
                        </button>
                    )}
                </div>

                {/* Email reminder — opens a preview/edit step before sending */}
                <div className="collect-adjustment-section">
                    <label className="collect-adjustment-label">Send them a payment link</label>
                    <button
                        type="button"
                        className="collect-adjustment-action"
                        onClick={() => setShowEmailReview(true)}
                        disabled={busy !== null || cardPaymentsBlocked}
                        title={cardPaymentsBlocked ? "Connect your payout account in Manage Finances first" : undefined}
                    >
                        <Icon path={mdiEmailFastOutline} size={0.8} />
                        Email payment reminder
                    </button>
                </div>
                </>
                )}
            </div>
        </BaseModal>

        <EmailReviewModal
            isOpen={showEmailReview}
            emailType="adjustmentPayment"
            templateData={{
                firstName: studentName?.split(" ")[0] || "Student",
                // The preview backend recomputes amount + service fee + total from `amount`
                // (matching the send path), so we just pass the real subtotal + currency.
                amount: amountMinor,
                currency,
            }}
            audienceDescription={`To ${studentName || "this student"}`}
            recipientCount={1}
            onConfirm={handleConfirmEmailSend}
            onCancel={() => setShowEmailReview(false)}
            isConfirming={isConfirmingEmail}
        />
        </>
    );
}

export default CollectAdjustmentModal;
